import React, { useState, useRef, useEffect } from "react"
import {
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Alert,
  ListGroup,
  UncontrolledTooltip,
  Spinner,
  Collapse,
} from "reactstrap"
import {
  ejecutarMigracion,
  obtenerDisponibilidad,
  obtenerDisponibilidadLimpiezaCompleta,
  obtenerEstado,
} from "api/migracionApi"
import { formatearNumero } from "helpers/formatters"
import useAuthorization from "hooks/useAuthorization"
import useEstadoMigracion from "hooks/useEstadoMigracion"
import FuegosArtificiales from "./FuegosArtificiales"
import useLogsAcumulados, { MAX_LINEAS_LOG } from "hooks/useLogsAcumulados"
import BannerImportacionEnCurso from "./BannerImportacionEnCurso"
import DashboardMigracion from "./DashboardMigracion"
import FilaCapacidad from "./FilaCapacidad"
import {
  DetalleLimpiezaCompleta,
  FilaLimpiezaCompleta,
  fraseCoincide,
} from "./LimpiezaCompleta"
import ProgresoImportacion from "./ProgresoImportacion"
import ConfirmacionNiveles, { DetalleNiveles } from "./ConfirmacionNiveles"
import SelectorNiveles from "./SelectorNiveles"
import { corridaVivaDe } from "./corridaVivaHelpers"
import {
  SELECCION_POR_DEFECTO,
  alternarNivel,
  DESCRIPCION_RECALCULAR_SCORING,
  ETIQUETA_RECALCULAR_SCORING,
  capacidadNivelesDe,
  capacidadScoringDe,
  construirParametros,
  decidirEnvio,
  efectivaDe,
  opcionesProductoresHabilitadas,
} from "./nivelesImportacion"
import {
  ESTADO,
  TEXTOS,
  UMBRAL_SIN_NOTICIAS_SEG,
  esEstadoDesbloqueo,
  esEstadoFinal,
  esImportacionExitosa,
  TEXTO_EXITO_IMPORTACION,
  esSeguimientoPerdido,
  recalculoScoringCortado,
} from "./migracionEstadoHelpers"

const BADGE_OPT = { color: "secondary", texto: "opt" }

// BDSJEA-353: textos que explican qué pasa al tildar y al no tildar cada check.
const DESCRIPCION_VALIDAR_CUIT =
  "Verifica que el CUIT tenga 11 dígitos (ignora guiones y espacios). Tildado: descarta las filas con CUIT inválido y las informa como «Descartados». Sin tildar: esas filas fallan al guardarse y la corrida se corta con errores antes de importar los establecimientos."

const DESCRIPCION_DUPLICADOS = (
  <>
    <span className="d-block">
      Tildado: si el CUIT ya existe, no lo modifica y lo informa como «Ya
      existían».
    </span>
    <span className="d-block">
      Sin tildar: lo actualiza (en la importación solo le asigna el id de
      cliente de origen y, si no tenía, la entidad) y lo informa como
      «Actualizados».
    </span>
  </>
)

const DESCRIPCION_EXCLUIR_ARCA = (
  <>
    <span className="d-block">
      Tildado: no consulta ARCA; los productores quedan sin datos de ARCA y se
      pueden sincronizar después. Los establecimientos sin detalle se crean con
      provincia San Juan.
    </span>
    <span className="d-block">
      Sin tildar: consulta ARCA para cada productor (≈0,6-0,9 s c/u; entre 3 y 4
      horas)
    </span>
  </>
)

const TEXTO_NOTA_EXCLUSIONES =
  "Siempre se excluyen, sin contarse como error, las filas sin razón social, sin CUIT cargado o con CUIT repetido; figuran en «Excluidos» y en el CSV de excluidos. Un CUIT escrito pero inválido no se excluye (ver «Validación automática de CUITs»)."

// BDSJEA-245: clave técnica (obj_route) del permiso adicional "Importación
// Masiva" (obj_tipo = 100) que siembra el backend. Tiene que coincidir 1:1 con
// la migración SQL; el perfil que no lo tenga no ve el botón ni el historial.
export const PERMISO_IMPORTACION_MASIVA = "/agro/importacion-masiva"

// BDSJEA-441: por qué una opción no se puede usar sin Productores.
const NOTA_SOLO_PRODUCTORES = "Disponible solo si «Productores» está marcado."

const FASE = {
  IDLE: "idle",
  EJECUTANDO: "ejecutando",
  COMPLETADO: "completado",
  HISTORIAL: "historial",
}

const BotonImportacionMasiva = ({ className = "" }) => {
  const { puedeModificarObjeto } = useAuthorization()
  const [modalOpen, setModalOpen] = useState(false)
  const [fase, setFase] = useState(FASE.IDLE)
  const [validarCuit, setValidarCuit] = useState(false)
  const [detectarDuplicados, setDetectarDuplicados] = useState(false)
  const [excluirArca, setExcluirArca] = useState(false)
  // BDSJEA-441: selección por niveles. `marcadas` = lo que tocó el usuario (la
  // cascada y el bloqueo salen de ahí). `capacidadNiveles`: null = consultando,
  // true = el backend declara `niveles` en /disponibilidad, false = no (sin
  // selector y se lanza como siempre: fail-closed).
  const [marcadas, setMarcadas] = useState(SELECCION_POR_DEFECTO)
  const [capacidadNiveles, setCapacidadNiveles] = useState(null)
  // BDSJEA-454: «Recalcular scoring al finalizar» (opt-in). Solo se ofrece si
  // /disponibilidad declara `scoring` Y `niveles`; nunca queda armada.
  const [recalcularScoring, setRecalcularScoring] = useState(false)
  const [capacidadScoring, setCapacidadScoring] = useState(false)
  const [estadoPrevio, setEstadoPrevio] = useState(null)
  const [confirmandoNiveles, setConfirmandoNiveles] = useState(false)
  // Lo que se pidió en la corrida en curso: sirve para avisar si el estado que
  // devuelve el backend no refleja la exclusión (backend sin soporte).
  const [excluirArcaPedido, setExcluirArcaPedido] = useState(false)
  const {
    logs,
    total: totalLogs,
    agregar: agregarLog,
    volcar: volcarLogs,
    limpiar: limpiarLogs,
  } = useLogsAcumulados()
  const [errorEjecucion, setErrorEjecucion] = useState(null)
  const [corridaCompletada, setCorridaCompletada] = useState(null)
  const [corridaPrevia, setCorridaPrevia] = useState(null)
  // BDSJEA-468: celebración al terminar bien; una sola vez por corrida.
  const [celebrar, setCelebrar] = useState(false)
  const celebradaRef = useRef(null)
  // "Iniciar" queda deshabilitado hasta conocer la corrida previa (si la
  // consulta falla, igual se habilita: solo se pierde esa protección).
  const [corridaPreviaLista, setCorridaPreviaLista] = useState(false)
  // BDSJEA-353: limpieza completa. `disponibilidad` viene del backend (null =
  // no consultada o falló: la opción no se muestra).
  const [disponibilidad, setDisponibilidad] = useState(null)
  const [limpiezaMarcada, setLimpiezaMarcada] = useState(false)
  const [fraseLimpieza, setFraseLimpieza] = useState("")
  const [confirmandoLimpieza, setConfirmandoLimpieza] = useState(false)
  // Re-enganche: el modal sigue una corrida que ya estaba viva en el servidor
  // (sin SSE ni salida detallada). `corridaViva` es el JSON crudo de /estado de
  // la última consulta (bloquea "Iniciar"); `estadoSemilla` evita mostrar
  // "Iniciando importación (0 s)" hasta la primera respuesta del polling.
  const [reenganchada, setReenganchada] = useState(false)
  const [corridaViva, setCorridaViva] = useState(null)
  const [estadoSemilla, setEstadoSemilla] = useState(null)
  const [avisoSse, setAvisoSse] = useState(null)
  const [avisoIdle, setAvisoIdle] = useState(null)
  const [sseTerminado, setSseTerminado] = useState(false)
  const [verLog, setVerLog] = useState(false)
  const [segundosLocales, setSegundosLocales] = useState(0)
  const cancelarRef = useRef(null)
  const logRef = useRef(null)
  const estadoRef = useRef(null)
  const corridaDoneRef = useRef(null)
  const montadoRef = useRef(true)
  const modalOpenRef = useRef(false)
  const faseRef = useRef(FASE.IDLE)
  const corridaPreviaRef = useRef(null)
  const inicioRef = useRef(0)
  const consultaPreviaRef = useRef(0)
  // Al abrir el modal una corrida viva se re-engancha sola; se baja cuando el
  // usuario elige no seguirla ("Dejar de seguir", "Ver importaciones
  // anteriores") y se vuelve a subir al cerrar el modal o al iniciar.
  const autoReengancheRef = useRef(true)
  const reenganchadaRef = useRef(false)
  const corridaVivaRef = useRef(null)
  // Estado del hook vigente al re-enganchar o iniciar: el hook solo lo limpia en
  // su efecto (un render tarde), así que un estado final viejo de otra corrida
  // no debe ganar en el primer render de EJECUTANDO. Cada poll devuelve un
  // objeto nuevo, por lo que la identidad distingue el estado viejo del nuevo.
  const estadoDescartadoRef = useRef(null)
  // BDSJEA-441: la selección de niveles que se lanzó. Si el servicio rechaza
  // antes de lanzar (400, 409 de prerrequisito, 503: no se lanzó nada) se
  // restaura; tras un lanzamiento exitoso se descarta.
  const marcadasLanzadasRef = useRef(null)
  const scoringLanzadoRef = useRef(false)
  faseRef.current = fase
  modalOpenRef.current = modalOpen
  corridaPreviaRef.current = corridaPrevia
  reenganchadaRef.current = reenganchada
  corridaVivaRef.current = corridaViva

  // Al desmontar: se aborta el SSE y los callbacks dejan de actuar (el polling
  // lo detiene el propio hook).
  useEffect(() => {
    montadoRef.current = true
    return () => {
      montadoRef.current = false
      if (cancelarRef.current) cancelarRef.current()
    }
  }, [])

  const {
    estado,
    fallosConsecutivos,
    ultimoError,
    prohibido,
    reintentar,
    detener,
  } = useEstadoMigracion({ activo: fase === FASE.EJECUTANDO, corridaPrevia })
  estadoRef.current = estado
  const estadoHook = estado === estadoDescartadoRef.current ? null : estado

  // El alert de "no se pudo consultar" aparece tras 3 fallos seguidos (o de
  // inmediato si ya terminó el SSE y falló el GET final, o ante un 403).
  const errorConsulta =
    ultimoError &&
    (prohibido ||
      fallosConsecutivos >= 3 ||
      (sseTerminado && fallosConsecutivos >= 1))
      ? ultimoError
      : null

  // Hasta la primera respuesta del polling, una corrida re-enganchada se
  // muestra con el estado crudo con el que se re-enganchó.
  const estadoVisible = estadoHook ?? (reenganchada ? estadoSemilla : null)
  const estadoEfectivo = estadoVisible?.estadoEfectivo
  // Sin SSE (re-enganche) nunca hay salida en vivo: el aviso no aplica.
  const sinNoticias =
    fase === FASE.EJECUTANDO &&
    !reenganchada &&
    segundosLocales >= UMBRAL_SIN_NOTICIAS_SEG &&
    (!estadoVisible || estadoEfectivo === ESTADO.INICIANDO) &&
    logs.length === 0

  // "Desbloqueado" (se puede cerrar el modal) es distinto de FASE.COMPLETADO:
  // el proceso no terminó pero el usuario no puede quedar atrapado.
  const desbloqueado =
    fase === FASE.EJECUTANDO &&
    (esEstadoDesbloqueo(estadoEfectivo) || !!errorConsulta || sinNoticias)
  // Re-enganchada no hay SSE que perder: se puede cerrar y volver a abrir.
  const esBloqueante =
    fase === FASE.EJECUTANDO && !desbloqueado && !reenganchada

  const toggle = () => {
    if (esBloqueante) return
    if (modalOpen && fase === FASE.EJECUTANDO) {
      handleClose()
      return
    }
    setModalOpen(prev => !prev)
  }

  const reiniciarLimpieza = () => {
    setLimpiezaMarcada(false)
    setFraseLimpieza("")
    setConfirmandoLimpieza(false)
  }

  const conNiveles = capacidadNiveles === true
  // Solo con la capacidad `scoring` (y `niveles`) la casilla existe y viaja.
  const scoringActivo = conNiveles && capacidadScoring && recalcularScoring
  const efectiva = efectivaDe(marcadas)
  // Las opciones solo-Productores rigen únicamente con Productores efectivo (sin
  // la capacidad de niveles el modal es el de siempre: siempre rigen).
  const opcionesProductores = conNiveles
    ? opcionesProductoresHabilitadas(efectiva)
    : true

  // La limpieza solo rige si el backend la ofrece, el usuario la marcó y hay
  // Productores en la selección.
  const limpiezaActiva =
    limpiezaMarcada &&
    disponibilidad?.disponible === true &&
    opcionesProductores
  const fraseOk =
    limpiezaActiva && fraseCoincide(fraseLimpieza, disponibilidad?.frasePedida)

  const marcarLimpieza = marcada => {
    setLimpiezaMarcada(marcada)
    // Al desmarcar se vuelve al comportamiento normal: sin frase.
    if (!marcada) {
      setFraseLimpieza("")
      setConfirmandoLimpieza(false)
    }
  }

  const reiniciarSeleccion = () => {
    setMarcadas(SELECCION_POR_DEFECTO)
    setRecalcularScoring(false)
    setConfirmandoNiveles(false)
  }

  // Marca/desmarca un nivel. Al quedar sin Productores se apagan (y dejan de
  // enviarse) las opciones solo-Productores y la limpieza completa.
  const alternarCasilla = nivel => {
    const siguiente = alternarNivel(marcadas, nivel)
    setMarcadas(siguiente)
    if (!opcionesProductoresHabilitadas(efectivaDe(siguiente))) {
      setValidarCuit(false)
      setDetectarDuplicados(false)
      setExcluirArca(false)
      reiniciarLimpieza()
    }
  }

  // Con la limpieza activa, "Iniciar" pide primero la confirmación reforzada
  // (con el detalle por nivel); por niveles sin limpieza, el diálogo por nivel.
  const pedirInicio = () => {
    if (limpiezaActiva) {
      if (fraseOk) setConfirmandoLimpieza(true)
      return
    }
    if (conNiveles && efectiva.length > 0) {
      setConfirmandoNiveles(true)
      return
    }
    iniciarImportacion()
  }

  const resetModal = () => {
    setFase(FASE.IDLE)
    limpiarLogs()
    setErrorEjecucion(null)
    setCorridaCompletada(null)
    setAvisoSse(null)
    setAvisoIdle(null)
    setSseTerminado(false)
    setVerLog(false)
    setValidarCuit(false)
    setDetectarDuplicados(false)
    setExcluirArca(false)
    setExcluirArcaPedido(false)
    setReenganchada(false)
    setCorridaViva(null)
    setEstadoSemilla(null)
    reiniciarLimpieza()
    reiniciarSeleccion()
  }

  const handleClose = () => {
    detener()
    if (cancelarRef.current) cancelarRef.current()
    autoReengancheRef.current = true
    resetModal()
    setCorridaPreviaLista(false)
    setModalOpen(false)
  }

  const iniciarImportacion = () => {
    // Guarda contra doble clic: solo se inicia desde IDLE y con la corrida
    // previa ya resuelta. La ref se actualiza ya mismo (antes del re-render).
    if (faseRef.current !== FASE.IDLE || !corridaPreviaLista) return
    // Nunca se inicia con una corrida viva (re-enganchada o vista al abrir).
    if (reenganchadaRef.current || corridaVivaRef.current) return
    if (limpiezaActiva && !fraseOk) return
    setConfirmandoNiveles(false)
    // BDSJEA-441, fail-closed: con la capacidad `niveles` se manda siempre la
    // selección; sin ella, la llamada histórica. Si había una selección que ya
    // no se puede respetar, no se llama a la API.
    const decision = decidirEnvio({
      capacidadNiveles: capacidadNiveles === true,
      marcadas,
    })
    if (decision.tipo === "bloqueado") {
      setErrorEjecucion(decision.mensaje)
      return
    }
    if (decision.tipo === "vacio") return
    estadoDescartadoRef.current = estadoRef.current
    // Con las opciones apagadas y sin niveles la llamada es idéntica a la de
    // siempre.
    const params = construirParametros({
      niveles: decision.tipo === "niveles" ? decision.niveles : null,
      validarCuit,
      detectarDuplicados,
      excluirArca,
      recalcularScoring: scoringActivo,
      limpiezaActiva,
      confirmacionLimpieza: fraseLimpieza,
    })
    // Si el servicio responde 409 (otra corrida viva) el modal se re-engancha.
    autoReengancheRef.current = true
    setExcluirArcaPedido(params.excluirArca === true)
    // Una opción destructiva (y una selección parcial) nunca queda armada para
    // la próxima vez.
    reiniciarLimpieza()
    marcadasLanzadasRef.current = marcadas
    scoringLanzadoRef.current = scoringActivo
    reiniciarSeleccion()
    faseRef.current = FASE.EJECUTANDO
    inicioRef.current = Date.now()
    setCorridaPreviaLista(false)
    setFase(FASE.EJECUTANDO)
    limpiarLogs()
    setErrorEjecucion(null)
    setAvisoSse(null)
    setAvisoIdle(null)
    setSseTerminado(false)
    setSegundosLocales(0)
    corridaDoneRef.current = null

    cancelarRef.current = ejecutarMigracion(
      params,
      linea => {
        if (montadoRef.current) agregarLog(linea)
      },
      // `done` del SSE: solo dispara un GET final. Pasar a COMPLETADO lo
      // decide el estado final del polling, nunca el exitCode del stream.
      ({ corrida } = {}) => {
        if (!montadoRef.current) return
        volcarLogs()
        corridaDoneRef.current = corrida ?? null
        setSseTerminado(true)
        reintentar()
      },
      msg => {
        if (!montadoRef.current) return
        volcarLogs()
        // Texto del backend tal cual; el genérico solo si viene vacío/null.
        const mensaje =
          typeof msg === "string" && msg.trim() ? msg : TEXTOS.falloGenerico
        // BDSJEA-357: se cortó solo el seguimiento en vivo (código estable del
        // backend); la importación sigue. Se queda en EJECUTANDO con el texto
        // del backend (sin prefijo duplicado) y el polling decide la fase.
        if (esSeguimientoPerdido(mensaje)) {
          setSseTerminado(true)
          setAvisoSse(mensaje)
          reintentar()
          return
        }
        const actual = estadoRef.current
        // Solo cuenta como "la nuestra" una corrida viva distinta de la previa.
        // Un EN_CURSO de la corrida previa es otra importación en curso (el 409
        // del micro llega como evento SSE error): se muestra su mensaje y se
        // vuelve a IDLE, no se lo confunde con una conexión cortada.
        const vivo =
          actual?.estadoEfectivo === ESTADO.EN_CURSO ||
          actual?.estadoEfectivo === ESTADO.SIN_RESPUESTA
        const sigueVivo = vivo && actual?.corrida !== corridaPreviaRef.current
        if (sigueVivo) {
          // Se cortó solo la salida en vivo: el proceso sigue en el servidor.
          setSseTerminado(true)
          setAvisoSse(`${TEXTOS.sseCortado} (${mensaje})`)
          reintentar()
        } else {
          detener()
          setErrorEjecucion(mensaje)
          // Rechazo previo al lanzamiento: la selección de niveles se
          // conserva (la limpieza completa sigue sin quedar armada).
          if (marcadasLanzadasRef.current) {
            setMarcadas(marcadasLanzadasRef.current)
            setRecalcularScoring(scoringLanzadoRef.current)
            marcadasLanzadasRef.current = null
          }
          setFase(FASE.IDLE)
        }
      },
      // BDSJEA-345: el stream cerró sin `done` ni `error`. Es solo un aviso:
      // el estado real (y si se pasa a COMPLETADO) lo decide el polling de
      // /estado, que se consulta ya mismo. No se muestra ningún exitCode.
      () => {
        if (!montadoRef.current) return
        volcarLogs()
        setSseTerminado(true)
        setAvisoSse(TEXTOS.sseSinResultado)
        reintentar()
      },
    )
  }

  // Sigue una corrida que ya estaba viva en el servidor, sin llamar a
  // ejecutarMigracion. React 17 no agrupa los setState posteriores a un await:
  // el orden importa. Primero todo lo que no cambia la vista (IDLE/HISTORIAL
  // no lo usan, "Iniciar" sigue bloqueado) y AL FINAL la fase.
  const reenganchar = data => {
    estadoDescartadoRef.current = estadoRef.current
    marcadasLanzadasRef.current = null
    setReenganchada(true)
    setCorridaPrevia(null)
    setEstadoPrevio(null)
    reiniciarLimpieza()
    reiniciarSeleccion()
    setExcluirArcaPedido(false)
    setCorridaViva(data)
    setEstadoSemilla(data)
    // Hasta la próxima consulta de apertura "Iniciar" no debe poder habilitarse.
    setCorridaPreviaLista(false)
    limpiarLogs()
    setAvisoSse(null)
    setAvisoIdle(null)
    setSseTerminado(false)
    setVerLog(false)
    corridaDoneRef.current = null
    const transcurrido = data?.monitor?.transcurridoSeg
    const previo =
      typeof transcurrido === "number" && transcurrido > 0 ? transcurrido : 0
    inicioRef.current = Date.now() - previo * 1000
    setSegundosLocales(Math.floor(previo))
    faseRef.current = FASE.EJECUTANDO
    setFase(FASE.EJECUTANDO)
  }

  // Desde la vista re-enganchada: no seguirla más y mirar el historial. Al
  // volver, el IDLE muestra el banner y "Iniciar" queda deshabilitado.
  const verHistorialDesdeReenganche = () => {
    autoReengancheRef.current = false
    setErrorEjecucion(null)
    setReenganchada(false)
    setFase(FASE.HISTORIAL)
  }

  const verProgresoDesdeBanner = data => {
    setErrorEjecucion(null)
    reenganchar(data)
  }

  const dejarDeSeguir = () => {
    autoReengancheRef.current = false
    detener()
    if (cancelarRef.current) cancelarRef.current()
    setFase(FASE.IDLE)
    limpiarLogs()
    setAvisoSse(null)
    setSseTerminado(false)
    setAvisoIdle(TEXTOS.sigueEnServidor)
  }

  // Al abrir el modal en IDLE se consulta una única vez el estado para conocer
  // la corrida previa (tolerante a fallo: sin ese dato solo se pierde la
  // protección contra confundir la corrida anterior con la nueva).
  useEffect(() => {
    if (!modalOpen || fase !== FASE.IDLE) return
    const consulta = ++consultaPreviaRef.current
    // La respuesta no se descarta al cambiar de fase mientras el modal siga
    // abierto; solo si se desmontó, se cerró el modal, ya hay otra consulta o
    // la fase dejó de ser IDLE (la corrida previa debe ser la de antes de iniciar).
    const vigente = () =>
      montadoRef.current &&
      modalOpenRef.current &&
      faseRef.current === FASE.IDLE &&
      consultaPreviaRef.current === consulta
    ;(async () => {
      let previa = null
      let viva = null
      let previo = null
      try {
        const data = await obtenerEstado()
        previa = data?.corrida ?? null
        // Se decide con el JSON crudo, no con el estado resuelto del hook.
        viva = corridaVivaDe(data)
        // BDSJEA-441: el estado de la última corrida aporta los conteos
        // «la última importación cargó N» del diálogo de confirmación.
        previo = data && typeof data === "object" ? data : null
      } catch {
        previa = null
      }
      if (!vigente()) return
      if (viva) {
        if (autoReengancheRef.current) {
          reenganchar(viva)
          return
        }
        // Sin re-enganche automático: banner en IDLE e "Iniciar" bloqueado.
        // corridaViva ANTES de corridaPreviaLista (sin tramo con Iniciar habilitado).
        setCorridaViva(viva)
        setCorridaPrevia(null)
        setEstadoPrevio(null)
        setCorridaPreviaLista(true)
        return
      }
      setCorridaViva(null)
      setCorridaPrevia(previa)
      setEstadoPrevio(previo)
      setCorridaPreviaLista(true)
    })()
  }, [modalOpen, fase])

  // BDSJEA-353: ¿se ofrece la limpieza completa? Tolerante a fallo: si la
  // consulta falla o dice que no, la opción no se muestra y la importación
  // normal sigue intacta.
  useEffect(() => {
    if (!modalOpen || fase !== FASE.IDLE) return
    // No mostrar la frase/ambiente de una consulta anterior mientras se
    // reconsulta: hasta que responda el backend la opción no se ofrece.
    setDisponibilidad(null)
    let vigente = true
    ;(async () => {
      let d = null
      try {
        d = await obtenerDisponibilidadLimpiezaCompleta()
      } catch {
        d = null
      }
      if (!vigente || !montadoRef.current) return
      const valida = d && typeof d === "object" ? d : null
      setDisponibilidad(valida)
      if (valida?.disponible !== true) reiniciarLimpieza()
    })()
    return () => {
      vigente = false
    }
  }, [modalOpen, fase])

  // BDSJEA-441: ¿se ofrece el selector por niveles? Solo si /disponibilidad lo
  // declara (`niveles === true`). Cualquier falla de la consulta (API vieja,
  // timeout, 403, respuesta rara) equivale a "no": sin selector y lanzamiento
  // histórico (fail-closed).
  useEffect(() => {
    if (!modalOpen || fase !== FASE.IDLE) return undefined
    setCapacidadNiveles(null)
    setCapacidadScoring(false)
    let vigente = true
    ;(async () => {
      let ok = false
      let conScoring = false
      try {
        const d = await obtenerDisponibilidad()
        ok = capacidadNivelesDe(d)
        conScoring = capacidadScoringDe(d)
      } catch {
        ok = false
        conScoring = false
      }
      if (!vigente || !montadoRef.current) return
      setCapacidadScoring(conScoring)
      setCapacidadNiveles(ok)
    })()
    return () => {
      vigente = false
    }
  }, [modalOpen, fase])

  // Cronómetro local (antes de la primera respuesta): se calcula con
  // timestamps, no contando ticks, para no atrasarse con la pestaña en segundo plano.
  useEffect(() => {
    if (fase !== FASE.EJECUTANDO) return undefined
    const id = setInterval(
      () =>
        setSegundosLocales(
          Math.max(0, Math.floor((Date.now() - inicioRef.current) / 1000)),
        ),
      1000,
    )
    return () => clearInterval(id)
  }, [fase])

  // Solo un estado final del polling lleva a COMPLETADO.
  useEffect(() => {
    if (
      fase === FASE.EJECUTANDO &&
      estadoHook &&
      esEstadoFinal(estadoHook.estadoEfectivo)
    ) {
      setCorridaCompletada(estadoHook.corrida ?? corridaDoneRef.current)
      setCorridaPrevia(estadoHook.corrida ?? null)
      setFase(FASE.COMPLETADO)
      const claveCorrida = estadoHook.corrida ?? corridaDoneRef.current
      if (
        esImportacionExitosa(estadoHook) &&
        celebradaRef.current !== claveCorrida
      ) {
        celebradaRef.current = claveCorrida
        setCelebrar(true)
      }
    }
  }, [fase, estadoHook])

  useEffect(() => {
    if (logRef.current) {
      logRef.current.scrollTop = logRef.current.scrollHeight
    }
  }, [logs])

  // Sin el permiso especial no se muestra nada (ni botón ni modal/historial).
  if (!puedeModificarObjeto(PERMISO_IMPORTACION_MASIVA)) return null

  const renderLog = () => (
    <div className="mt-3">
      <Button
        color="link"
        size="sm"
        className="p-0 text-muted"
        onClick={() => setVerLog(v => !v)}
        aria-expanded={verLog}
      >
        <i
          className={`mdi ${verLog ? "mdi-chevron-down" : "mdi-chevron-right"} me-1`}
        />
        {verLog ? "Ocultar salida detallada" : "Ver salida detallada"}
      </Button>
      <Collapse isOpen={verLog}>
        <div
          ref={logRef}
          style={{
            fontFamily: "monospace",
            fontSize: 12,
            background: "#1e1e1e",
            color: "#d4d4d4",
            padding: 12,
            borderRadius: 4,
            height: 300,
            overflowY: "auto",
            whiteSpace: "pre-wrap",
            wordBreak: "break-all",
          }}
        >
          {logs.length === 0 ? (
            <span style={{ color: "#888" }}>Sin salida todavía.</span>
          ) : (
            <>
              {totalLogs > logs.length && (
                <div style={{ color: "#b8b8b8" }}>
                  {`Se muestran las últimas ${formatearNumero(MAX_LINEAS_LOG)} líneas de ${formatearNumero(totalLogs)}.`}
                </div>
              )}
              {logs.map((l, i) => (
                <div key={totalLogs - logs.length + i}>{l}</div>
              ))}
            </>
          )}
        </div>
      </Collapse>
    </div>
  )

  return (
    <>
      <Button
        color="outline-primary"
        onClick={toggle}
        className={className}
        id="btn-importacion-masiva"
        disabled={esBloqueante}
      >
        <i className="mdi mdi-database-import-outline me-2"></i>
        Importación Masiva
      </Button>
      <UncontrolledTooltip
        target="btn-importacion-masiva"
        placement="top"
        fade={false}
      >
        Importar productores desde base de datos externa
      </UncontrolledTooltip>

      {celebrar && (
        <FuegosArtificiales
          texto={TEXTO_EXITO_IMPORTACION}
          onFin={() => setCelebrar(false)}
        />
      )}

      <Modal
        isOpen={modalOpen}
        toggle={toggle}
        size="xl"
        backdrop={esBloqueante ? "static" : true}
      >
        <ModalHeader toggle={!esBloqueante ? handleClose : undefined}>
          {fase === FASE.HISTORIAL ? (
            <>
              <i className="mdi mdi-history me-2 text-primary"></i>Historial de
              importaciones
            </>
          ) : (
            <>
              <i className="mdi mdi-database-import-outline me-2 text-primary"></i>
              Importación Masiva de Productores
              {fase === FASE.EJECUTANDO && (
                <Spinner size="sm" color="primary" className="ms-2" />
              )}
            </>
          )}
        </ModalHeader>

        <ModalBody>
          {/* ---- IDLE: configuración ---- */}
          {fase === FASE.IDLE && (
            <>
              {corridaViva && (
                <BannerImportacionEnCurso
                  estado={corridaViva}
                  onVerProgreso={verProgresoDesdeBanner}
                />
              )}
              <div className="d-flex justify-content-end mb-2">
                <Button
                  color="link"
                  size="sm"
                  className="text-muted p-0"
                  onClick={() => setFase(FASE.HISTORIAL)}
                >
                  <i className="mdi mdi-history me-1" />
                  Ver importaciones anteriores
                </Button>
              </div>

              {avisoIdle && (
                <Alert
                  color="info"
                  fade={false}
                  toggle={() => setAvisoIdle(null)}
                >
                  <i className="mdi mdi-information-outline me-2" />
                  {avisoIdle}
                </Alert>
              )}

              {errorEjecucion && (
                <Alert
                  color="danger"
                  fade={false}
                  toggle={() => setErrorEjecucion(null)}
                >
                  <i className="mdi mdi-alert-circle-outline me-2" />
                  {errorEjecucion}
                </Alert>
              )}

              <Alert color="info" fade={false}>
                <i className="mdi mdi-information-outline me-2"></i>
                <strong>Importación</strong>
                <p className="mb-0 mt-2">
                  Esta funcionalidad permite importar productores agropecuarios
                  desde su base de datos existente de manera masiva.
                </p>
              </Alert>

              {capacidadNiveles === false && (
                <small
                  className="text-muted d-block mb-2"
                  data-testid="nota-niveles-no-disponible"
                >
                  La importación por niveles no está disponible en este momento.
                </small>
              )}

              {conNiveles && (
                <SelectorNiveles
                  marcadas={marcadas}
                  onAlternar={alternarCasilla}
                  excluirArca={excluirArca}
                />
              )}

              <h6 className="mt-4">
                <i className="mdi mdi-format-list-checks me-2"></i>
                Capacidades del módulo
              </h6>

              <ListGroup className="mt-3">
                <FilaCapacidad
                  badge={BADGE_OPT}
                  titulo="Validación automática de CUITs"
                  descripcion={DESCRIPCION_VALIDAR_CUIT}
                  checkId="chk-validar-cuit"
                  checked={validarCuit}
                  onChange={setValidarCuit}
                  deshabilitada={!opcionesProductores}
                  nota={opcionesProductores ? null : NOTA_SOLO_PRODUCTORES}
                  testId="fila-validar-cuit"
                />
                <FilaCapacidad
                  badge={BADGE_OPT}
                  titulo="Detección de duplicados"
                  descripcion={DESCRIPCION_DUPLICADOS}
                  checkId="chk-detectar-duplicados"
                  checked={detectarDuplicados}
                  onChange={setDetectarDuplicados}
                  deshabilitada={!opcionesProductores}
                  nota={opcionesProductores ? null : NOTA_SOLO_PRODUCTORES}
                  testId="fila-detectar-duplicados"
                />
                <FilaLimpiezaCompleta
                  disponibilidad={disponibilidad}
                  marcado={limpiezaActiva}
                  onMarcar={marcarLimpieza}
                  deshabilitada={!opcionesProductores}
                  nota={opcionesProductores ? null : NOTA_SOLO_PRODUCTORES}
                />
                <FilaCapacidad
                  badge={BADGE_OPT}
                  titulo="Excluir ARCA"
                  descripcion={DESCRIPCION_EXCLUIR_ARCA}
                  checkId="chk-excluir-arca"
                  checked={excluirArca}
                  onChange={setExcluirArca}
                  deshabilitada={!opcionesProductores}
                  nota={opcionesProductores ? null : NOTA_SOLO_PRODUCTORES}
                  testId="fila-excluir-arca"
                />
                {conNiveles && capacidadScoring && (
                  <FilaCapacidad
                    badge={BADGE_OPT}
                    titulo={ETIQUETA_RECALCULAR_SCORING}
                    descripcion={DESCRIPCION_RECALCULAR_SCORING}
                    checkId="chk-recalcular-scoring"
                    tituloId="tit-recalcular-scoring"
                    inputProps={{ "aria-labelledby": "tit-recalcular-scoring" }}
                    checked={recalcularScoring}
                    onChange={setRecalcularScoring}
                    testId="fila-recalcular-scoring"
                  />
                )}
              </ListGroup>
              <small
                className="text-muted d-block mt-2"
                data-testid="nota-exclusiones"
              >
                {TEXTO_NOTA_EXCLUSIONES}
              </small>
              <DetalleLimpiezaCompleta
                disponibilidad={disponibilidad}
                marcado={limpiezaActiva}
                frase={fraseLimpieza}
                onFrase={setFraseLimpieza}
              />
            </>
          )}

          {/* ---- EJECUTANDO: progreso real + salida en vivo colapsada ---- */}
          {fase === FASE.EJECUTANDO && (
            <>
              {reenganchada ? (
                <Alert color="info" fade={false} className="py-2">
                  <Spinner size="sm" className="me-2" />
                  Hay una importación en curso en el servidor. Podés cerrar esta
                  ventana y volver a abrirla para ver el progreso.
                </Alert>
              ) : (
                <Alert color="warning" fade={false} className="py-2">
                  <Spinner size="sm" className="me-2" />
                  Importación en progreso — no cierre esta ventana.
                </Alert>
              )}
              {reenganchada && errorEjecucion && (
                <Alert color="info" fade={false} className="py-2">
                  <i className="mdi mdi-information-outline me-2" />
                  {errorEjecucion}
                </Alert>
              )}
              <ProgresoImportacion
                estado={estadoVisible}
                segundosLocales={segundosLocales}
                errorConsulta={errorConsulta}
                avisoSse={avisoSse}
                sinNoticias={sinNoticias}
                onReintentar={reintentar}
                excluirArcaPedido={excluirArcaPedido}
              />
              {!reenganchada && renderLog()}
            </>
          )}

          {/* ---- HISTORIAL: corridas anteriores ---- */}
          {fase === FASE.HISTORIAL && (
            <>
              <BannerImportacionEnCurso
                onVerProgreso={verProgresoDesdeBanner}
              />
              <DashboardMigracion />
            </>
          )}

          {/* ---- COMPLETADO: estado final + dashboard ---- */}
          {fase === FASE.COMPLETADO && (
            <>
              <ProgresoImportacion
                estado={estadoHook}
                segundosLocales={segundosLocales}
                excluirArcaPedido={excluirArcaPedido}
              />
              {!reenganchada && renderLog()}
              <DashboardMigracion
                corridaInicial={estadoHook?.corrida ?? corridaCompletada}
              />
            </>
          )}
        </ModalBody>

        <ModalFooter className="justify-content-between">
          <small className="text-muted">
            {fase === FASE.IDLE &&
              (validarCuit ||
                detectarDuplicados ||
                limpiezaActiva ||
                excluirArca ||
                scoringActivo) && (
                <>
                  <i className="mdi mdi-tune me-1" />
                  {[
                    validarCuit && "Validar CUITs",
                    detectarDuplicados && "Detectar duplicados",
                    limpiezaActiva && "Limpiar la base completa",
                    excluirArca && "Excluir ARCA",
                    scoringActivo && "Recalcular scoring",
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </>
              )}
          </small>
          <div className="d-flex gap-2">
            {fase === FASE.IDLE && (
              <>
                <Button color="secondary" onClick={handleClose}>
                  Cerrar
                </Button>
                <Button
                  color="primary"
                  onClick={pedirInicio}
                  disabled={
                    !corridaPreviaLista ||
                    capacidadNiveles === null ||
                    !!corridaViva ||
                    (limpiezaActiva && !fraseOk) ||
                    (conNiveles && efectiva.length === 0)
                  }
                >
                  <i className="mdi mdi-upload me-2"></i>
                  Iniciar Importación
                </Button>
              </>
            )}
            {fase === FASE.EJECUTANDO && (
              <>
                {desbloqueado && (
                  <Button color="primary" outline onClick={reintentar}>
                    <i className="mdi mdi-reload me-2" />
                    Reintentar
                  </Button>
                )}
                {reenganchada ? (
                  <Button
                    color="secondary"
                    outline
                    onClick={verHistorialDesdeReenganche}
                  >
                    <i className="mdi mdi-history me-2" />
                    Ver importaciones anteriores
                  </Button>
                ) : (
                  <Button color="secondary" outline onClick={dejarDeSeguir}>
                    <i className="mdi mdi-eye-off-outline me-2" />
                    Dejar de seguir
                  </Button>
                )}
                {(desbloqueado || reenganchada) && (
                  <Button color="primary" onClick={handleClose}>
                    Cerrar
                  </Button>
                )}
              </>
            )}
            {fase === FASE.HISTORIAL && (
              <>
                <Button
                  color="secondary"
                  outline
                  onClick={() => setFase(FASE.IDLE)}
                >
                  <i className="mdi mdi-arrow-left me-2" />
                  Volver
                </Button>
                <Button color="primary" onClick={handleClose}>
                  Cerrar
                </Button>
              </>
            )}
            {fase === FASE.COMPLETADO && (
              <>
                {/* BDSJEA-454: tras cortarse el recálculo de scoring no se
                    ofrece volver a importar (reimporta todo). */}
                {!recalculoScoringCortado(estadoVisible) && (
                  <Button color="secondary" outline onClick={resetModal}>
                    <i className="mdi mdi-reload me-2" />
                    Nueva importación
                  </Button>
                )}
                <Button color="primary" onClick={handleClose}>
                  Cerrar
                </Button>
              </>
            )}
          </div>
        </ModalFooter>
      </Modal>

      {/* BDSJEA-441: confirmación por nivel (qué se borra / reimporta / no se toca). */}
      <ConfirmacionNiveles
        isOpen={modalOpen && confirmandoNiveles}
        efectiva={efectiva}
        excluirArca={excluirArca}
        recalcularScoring={scoringActivo}
        estadoPrevio={estadoPrevio}
        onCancelar={() => setConfirmandoNiveles(false)}
        onConfirmar={() => {
          setConfirmandoNiveles(false)
          iniciarImportacion()
        }}
      />

      {/* BDSJEA-353: segundo paso antes de borrar toda la base. */}
      <Modal
        isOpen={modalOpen && confirmandoLimpieza}
        toggle={() => setConfirmandoLimpieza(false)}
        centered
        role="alertdialog"
        labelledBy="titulo-confirmar-limpieza"
        // reactstrap no deja pasar aria-describedby al diálogo: se asocia al abrir.
        onOpened={() =>
          document
            .querySelector('[aria-labelledby="titulo-confirmar-limpieza"]')
            ?.setAttribute("aria-describedby", "descripcion-confirmar-limpieza")
        }
        data-testid="confirmar-limpieza"
      >
        <ModalHeader toggle={() => setConfirmandoLimpieza(false)}>
          <i className="mdi mdi-alert-octagon-outline me-2 text-danger"></i>
          <span id="titulo-confirmar-limpieza">
            Confirmar limpieza completa
          </span>
        </ModalHeader>
        <ModalBody id="descripcion-confirmar-limpieza">
          <p className="fw-bold mb-2">
            {`¿Confirmás borrar TODA la base de ${disponibilidad?.ambiente ?? "este ambiente"}?`}
          </p>
          <p className="mb-2">
            Se borrarán los datos del ambiente y después se importará. Esta
            acción no se puede deshacer desde la aplicación.
          </p>
          {conNiveles && (
            <DetalleNiveles
              efectiva={efectiva}
              excluirArca={excluirArca}
              recalcularScoring={scoringActivo}
              estadoPrevio={estadoPrevio}
              limpiezaCompleta
            />
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={() => setConfirmandoLimpieza(false)}
          >
            Cancelar
          </Button>
          <Button
            color="danger"
            onClick={() => {
              setConfirmandoLimpieza(false)
              iniciarImportacion()
            }}
          >
            <i className="mdi mdi-delete-alert-outline me-2"></i>
            Sí, borrar todo e importar
          </Button>
        </ModalFooter>
      </Modal>
    </>
  )
}

export const IconoImportacionMasiva = () => {
  const [, setModalOpen] = useState(false)

  return (
    <>
      <Button
        color="link"
        className="text-primary p-1"
        onClick={() => setModalOpen(true)}
        id="icono-importacion"
      >
        <i className="mdi mdi-database-import-outline fs-4"></i>
      </Button>
      <UncontrolledTooltip target="icono-importacion" fade={false}>
        Importación masiva de productores
      </UncontrolledTooltip>

      <BotonImportacionMasiva />
    </>
  )
}

export default BotonImportacionMasiva
